import React, { useEffect, useState, useCallback } from 'react'
import { Card, Table, Button, Spinner, Badge, Dropdown } from 'react-bootstrap'
import {
  useCreateExportMutation,
  useDeleteExportMutation,
  useListExportsQuery,
} from '../../../services/exportApi'

import { Export, ExportType } from '../../../types/export'
import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import ConfirmDialog from '../../../components/modals/ConfirmDialog'
import { selectSelectedModelId } from '../../../store/slices/modelSlice'
import ExportButton from '../../../components/buttons/ExportButton'
import { useFetchModelsQuery } from '../../../services/modelsApi'
import ExportTypeDialog from '../../../components/modals/ExportTypeDialog'
import ErrorDialog from '../../../components/modals/ErrorDialog'

const StyledCard = styled(Card)`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  color: ${(props) => props.theme.textLight || '#fff'};
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  margin-bottom: 1rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  .card-header {
    background: transparent;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .card-body {
    background: transparent;
  }
`

const StyledTable = styled(Table)`
  color: #fff;
  margin: 0;
  border-collapse: separate;
  border-spacing: 0;

  thead th {
    background: rgba(42, 42, 42, 0.6);
    color: #ffffff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding: 0.75rem 1rem;
  }

  th,
  td {
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.08) !important;
    padding: 1rem;
    vertical-align: middle;
    background: transparent;
    color: #fff;
  }

  tbody tr:hover {
    background: rgba(0, 0, 0, 0.15);
  }

  .badge {
    font-size: 0.85rem;
    padding: 0.5em 0.85em;
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: transparent;
  }
`

interface ExportsSubViewProps {
  isModal?: boolean
  onClose?: () => void
}

const ExportsSubView: React.FC<ExportsSubViewProps> = ({ isModal = false, onClose }) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const dispatch = useAppDispatch()
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)
  const [showExportTypeDialog, setShowExportTypeDialog] = useState<boolean>(false)
  const [selectedExport, setSelectedExport] = useState<Export | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)

  const { data: modelsData } = useFetchModelsQuery(
    {
      project_id: selectedProjectId || '',
      page: 0,
      limit: 100,
      sort_key: 'created_at',
      sort_val: -1,
    },
    {
      skip: !selectedProjectId,
    }
  )

  const {
    data: exportsData,
    isLoading,
    error: apiError,
  } = useListExportsQuery(
    {
      page: 0,
      limit: 100,
      sort_key: 'created_at',
      sort_val: -1,
    },
    {
      pollingInterval: 60000,
    }
  )

  const showExportItem = useCallback(
    (exportItem: Export): boolean => {
      return (
        exportItem.project?.id === selectedProjectId ||
        modelsData?.models?.some((model) => model.id === exportItem.model?.id) ||
        exportItem.dataset?.id === project?.datasetid
      )
    },
    [selectedProjectId, modelsData?.models, project?.datasetid]
  )

  // Add effect to restore selected model from localStorage
  useEffect(() => {
    const savedModelId = localStorage.getItem('selectedModelId')
    const existsInModelsData = modelsData?.models?.some((model) => model.id === savedModelId)

    // No need to manage selected model state
  }, [dispatch, selectedModelId, modelsData, selectedProjectId])

  const [createExport] = useCreateExportMutation()
  const [deleteExport] = useDeleteExportMutation()

  const handleCreateExport = async (selectedTypes: ExportType[]): Promise<void> => {
    try {
      if (!project?.id || !project?.datasetid) {
        throw new Error('Project ID or Dataset ID is missing')
      }

      await createExport({
        name: `${project.name}-${new Date().toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}`,
        export_types: selectedTypes,
        project_id: project.id,
        dataset_id: project.datasetid,
        model_id: selectedModelId || undefined,
      })
    } catch (err) {
      setError(
        err
          ? (err as any).data?.message || 'Failed to create export'
          : 'An unexpected error occurred'
      )
      setShowError(true)
    }
  }

  const handleDeleteExport = async (id: string): Promise<void> => {
    try {
      await deleteExport(id)
      setShowDeleteConfirm(false)
      setSelectedExport(null)
    } catch (err) {
      setError(
        err
          ? (err as any).data?.message || 'Failed to delete export'
          : 'An unexpected error occurred'
      )
      setShowError(true)
    }
  }

  const initiateDelete = (export_: Export | undefined): void => {
    if (export_) {
      setSelectedExport(export_)
      setShowDeleteConfirm(true)
    }
  }

  const renderDownloadButton = (exportItem: Export): React.ReactNode => {
    // For single content key, render a simple ExportButton
    if (exportItem.content_keys.length === 1) {
      const filename = exportItem.content_keys[0].split('/').pop()
      return (
        <ExportButton
          exportId={exportItem.id}
          uri={exportItem.content_keys[0]}
          filename={filename}
          size="sm"
        >
          Download
        </ExportButton>
      )
    }

    // For multiple content keys, render a dropdown with ExportButtons
    return (
      <Dropdown>
        <Dropdown.Toggle variant="primary" size="sm">
          Download
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {exportItem.content_keys.map((key) => {
            const filename = key.split('/').pop()
            return (
              <Dropdown.Item key={key} className="p-2">
                <ExportButton
                  exportId={exportItem.id}
                  uri={key}
                  filename={filename}
                  showProgress={false}
                >
                  {filename}
                </ExportButton>
              </Dropdown.Item>
            )
          })}
        </Dropdown.Menu>
      </Dropdown>
    )
  }

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center">
        <Spinner animation="border" />
      </div>
    )
  }

  if (apiError) {
    return <div>Error loading exports</div>
  }

  const visibleExports = (exportsData?.exports || []).filter((e) => showExportItem(e))

  return (
    <>
      <StyledCard>
        <Card.Header className="d-flex justify-content-between align-items-center">
          {!isModal && <h4>Exports</h4>}
          {isModal && onClose && (
            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          )}
        </Card.Header>
        <Card.Body>
          {visibleExports.length === 0 ? (
            <div className="text-center text-muted" style={{ padding: '2rem 0' }}>
              No exports yet for this project.
            </div>
          ) : (
            <StyledTable hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>State</th>
                  <th>Created</th>
                  <th>Actions</th>
                  <th>Download</th>
                </tr>
              </thead>
              <tbody>
                {visibleExports.map((exportItem: Export) => (
                  <tr key={exportItem?.id}>
                    <td>
                      {exportItem?.name} - {exportItem?.model?.name}
                    </td>
                    <td>{exportItem?.types?.join(', ')}</td>
                    <td>
                      {exportItem?.state.toLowerCase() === 'running' ? (
                        <div className="d-flex align-items-center">
                          <Badge bg="warning">{exportItem?.state}</Badge>
                        </div>
                      ) : (
                        <Badge
                          bg={
                            exportItem?.state.toLowerCase() === 'complete' ? 'success' : 'warning'
                          }
                        >
                          {exportItem?.state}
                        </Badge>
                      )}
                    </td>
                    <td>{new Date(exportItem?.created_at || '').toLocaleDateString()}</td>
                    <td>
                      <Button variant="danger" size="sm" onClick={() => initiateDelete(exportItem)}>
                        Delete
                      </Button>
                    </td>
                    <td>
                      {exportItem?.state.toLowerCase() === 'complete' &&
                      exportItem.content_keys?.length ? (
                        renderDownloadButton(exportItem)
                      ) : (
                        <Button variant="primary" size="sm" disabled className="w-100">
                          Download
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          )}
        </Card.Body>
      </StyledCard>

      <ConfirmDialog
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirmRight={() => selectedExport && handleDeleteExport(selectedExport.id)}
        title="Delete Export"
        message={`Are you sure you want to delete export "${selectedExport?.name}"?`}
        confirmRightText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ExportTypeDialog
        show={showExportTypeDialog}
        onHide={() => setShowExportTypeDialog(false)}
        onConfirm={handleCreateExport}
      />

      <ErrorDialog show={showError} onHide={() => setShowError(false)} message={error} />
    </>
  )
}

export default ExportsSubView
